
import { Component, Vue } from 'vue-property-decorator'
import request from '@/utils/request'

@Component({
  name: 'ZohoIntegration'
})
export default class extends Vue {
  private activeTab = 'salesorders'
  private status: any = null
  private statusError = ''
  private statusLoading = false

  private salesOrders: any[] = []
  private salesOrdersLoading = false
  private salesOrderStatus = ''

  private items: any[] = []
  private itemsLoading = false
  private itemSearch = ''

  private contacts: any[] = []
  private contactsLoading = false
  private contactSearch = ''

  private invoices: any[] = []
  private invoicesLoading = false
  private invoiceStatus = ''

  private packages: any[] = []
  private packagesLoading = false
  private packageSearch = ''
  private packageShipmentStatus = ''
  private packageDetailsVisible = false
  private packageDetailsLoading = false
  private selectedPackage: any = null

  private webhookLogs: any[] = []
  private webhookLogsLoading = false
  private webhookLogType = ''
  private webhookLogStatus = ''
  private webhookLogsPagination = { total: 0, page: 1, per_page: 50, total_pages: 0 }
  private webhookLogDetailsVisible = false
  private selectedWebhookLog: any = null

  private orderDetailsVisible = false
  private selectedOrder: any = null

  // Sync related
  private syncDateRange: string[] = []
  private syncPreview: any = null
  private syncPreviewLoading = false
  private syncLoading = false
  private syncResults: any = null
  private syncResultsVisible = false
  private syncResultsTab = 'created'

  created() {
    this.checkStatus()
    this.loadSalesOrders()
  }

  private async checkStatus() {
    this.statusLoading = true
    this.statusError = ''
    try {
      const { data } = await request({
        url: '/zoho/status',
        method: 'get'
      })
      this.status = data
    } catch (err: any) {
      this.statusError = err.response?.data?.error || err.message
    } finally {
      this.statusLoading = false
    }
  }

  private async loadSalesOrders() {
    this.salesOrdersLoading = true
    try {
      const params: any = {}
      if (this.salesOrderStatus) {
        params.status = this.salesOrderStatus
      }
      const { data } = await request({
        url: '/zoho/salesorders',
        method: 'get',
        params
      })
      this.salesOrders = data.data?.salesorders || []
    } catch (err: any) {
      this.$message.error('Failed to load sales orders: ' + (err.response?.data?.error || err.message))
    } finally {
      this.salesOrdersLoading = false
    }
  }

  private async loadItems() {
    this.itemsLoading = true
    try {
      const params: any = {}
      if (this.itemSearch) {
        params.search = this.itemSearch
      }
      const { data } = await request({
        url: '/zoho/items',
        method: 'get',
        params
      })
      this.items = data.data?.items || []
    } catch (err: any) {
      this.$message.error('Failed to load items: ' + (err.response?.data?.error || err.message))
    } finally {
      this.itemsLoading = false
    }
  }

  private async loadContacts() {
    this.contactsLoading = true
    try {
      const params: any = {}
      if (this.contactSearch) {
        params.search = this.contactSearch
      }
      const { data } = await request({
        url: '/zoho/contacts',
        method: 'get',
        params
      })
      this.contacts = data.data?.contacts || []
    } catch (err: any) {
      this.$message.error('Failed to load contacts: ' + (err.response?.data?.error || err.message))
    } finally {
      this.contactsLoading = false
    }
  }

  private async loadInvoices() {
    this.invoicesLoading = true
    try {
      const params: any = {}
      if (this.invoiceStatus) {
        params.status = this.invoiceStatus
      }
      const { data } = await request({
        url: '/zoho/invoices',
        method: 'get',
        params
      })
      this.invoices = data.data?.invoices || []
    } catch (err: any) {
      this.$message.error('Failed to load invoices: ' + (err.response?.data?.error || err.message))
    } finally {
      this.invoicesLoading = false
    }
  }

  private async viewOrderDetails(order: any) {
    try {
      const { data } = await request({
        url: `/zoho/salesorders/${order.salesorder_id}`,
        method: 'get'
      })
      this.selectedOrder = data.data?.salesorder || order
      this.orderDetailsVisible = true
    } catch (err: any) {
      this.$message.error('Failed to load order details')
      this.selectedOrder = order
      this.orderDetailsVisible = true
    }
  }

  private handleTabClick(tab: any) {
    const name = tab.name
    if (name === 'salesorders' && this.salesOrders.length === 0) {
      this.loadSalesOrders()
    } else if (name === 'items' && this.items.length === 0) {
      this.loadItems()
    } else if (name === 'contacts' && this.contacts.length === 0) {
      this.loadContacts()
    } else if (name === 'invoices' && this.invoices.length === 0) {
      this.loadInvoices()
    } else if (name === 'packages' && this.packages.length === 0) {
      this.loadPackages()
    } else if (name === 'webhooklogs' && this.webhookLogs.length === 0) {
      this.loadWebhookLogs()
    }
  }

  private async loadPackages() {
    this.packagesLoading = true
    try {
      const params: any = {}
      if (this.packageSearch) {
        params.package_number = this.packageSearch
      }
      if (this.packageShipmentStatus) {
        params.shipment_status = this.packageShipmentStatus
      }
      const { data } = await request({
        url: '/zoho/packages',
        method: 'get',
        params
      })
      this.packages = data.data?.packages || []
    } catch (err: any) {
      this.$message.error('Failed to load packages: ' + (err.response?.data?.error || err.message))
    } finally {
      this.packagesLoading = false
    }
  }

  private async viewPackageDetails(pkg: any) {
    this.packageDetailsLoading = true
    this.selectedPackage = pkg
    this.packageDetailsVisible = true
    try {
      const { data } = await request({
        url: `/zoho/packages/${pkg.package_id}`,
        method: 'get'
      })
      this.selectedPackage = data.data?.package || pkg
    } catch (err: any) {
      this.$message.error('Failed to load package details: ' + (err.response?.data?.error || err.message))
    } finally {
      this.packageDetailsLoading = false
    }
  }

  private async loadWebhookLogs() {
    this.webhookLogsLoading = true
    try {
      const params: any = {
        page: this.webhookLogsPagination.page,
        per_page: this.webhookLogsPagination.per_page
      }
      if (this.webhookLogType) {
        params.webhook_type = this.webhookLogType
      }
      if (this.webhookLogStatus) {
        params.status = this.webhookLogStatus
      }
      const { data } = await request({
        url: '/zoho/webhook-logs',
        method: 'get',
        params
      })
      this.webhookLogs = data.data?.logs || []
      this.webhookLogsPagination = data.data?.pagination || this.webhookLogsPagination
    } catch (err: any) {
      this.$message.error('Failed to load webhook logs: ' + (err.response?.data?.error || err.message))
    } finally {
      this.webhookLogsLoading = false
    }
  }

  private handleWebhookLogsPageChange(page: number) {
    this.webhookLogsPagination.page = page
    this.loadWebhookLogs()
  }

  private viewWebhookLogDetails(log: any) {
    this.selectedWebhookLog = log
    this.webhookLogDetailsVisible = true
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    return date.toLocaleString('tr-TR')
  }

  private getWebhookStatusType(status: string): string {
    const types: Record<string, string> = {
      received: 'info',
      processing: 'warning',
      success: 'success',
      failed: 'danger'
    }
    return types[status?.toLowerCase()] || 'info'
  }

  private getStatusType(status: string): string {
    const types: Record<string, string> = {
      confirmed: 'success',
      fulfilled: 'success',
      shipped: 'primary',
      pending: 'warning',
      cancelled: 'danger',
      draft: 'info'
    }
    return types[status?.toLowerCase()] || 'info'
  }

  private getInvoiceStatusType(status: string): string {
    const types: Record<string, string> = {
      paid: 'success',
      sent: 'primary',
      overdue: 'danger',
      draft: 'info',
      partially_paid: 'warning'
    }
    return types[status?.toLowerCase()] || 'info'
  }

  private getShipmentStatusType(status: string): string {
    const types: Record<string, string> = {
      shipped: 'success',
      delivered: 'success',
      not_shipped: 'warning',
      pending: 'warning'
    }
    return types[status?.toLowerCase()] || 'warning'
  }

  private async previewSync() {
    this.syncPreviewLoading = true
    this.syncPreview = null
    try {
      const params: any = {}
      if (this.syncDateRange && this.syncDateRange.length === 2) {
        params.date_start = this.syncDateRange[0]
        params.date_end = this.syncDateRange[1]
      }
      const { data } = await request({
        url: '/zoho/sync-preview',
        method: 'get',
        params
      })
      this.syncPreview = data.data
    } catch (err: any) {
      this.$message.error('Failed to preview sync: ' + (err.response?.data?.error || err.message))
    } finally {
      this.syncPreviewLoading = false
    }
  }

  private async executeSync() {
    if (!this.syncDateRange || this.syncDateRange.length !== 2) {
      this.$message.warning('Please select a date range first')
      return
    }

    try {
      await this.$confirm(
        'This will sync all packages within the selected date range to the system. Continue?',
        'Confirm Sync',
        {
          confirmButtonText: 'Yes, Sync',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      )
    } catch {
      return // User cancelled
    }

    this.syncLoading = true
    try {
      const { data } = await request({
        url: '/zoho/sync-packages',
        method: 'post',
        data: {
          date_start: this.syncDateRange[0],
          date_end: this.syncDateRange[1]
        }
      })
      this.syncResults = data.data
      this.syncResultsVisible = true
      this.syncResultsTab = 'created'
      this.$message.success(`Sync completed: ${data.data?.summary?.created || 0} created, ${data.data?.summary?.updated || 0} updated`)
    } catch (err: any) {
      this.$message.error('Sync failed: ' + (err.response?.data?.error || err.message))
    } finally {
      this.syncLoading = false
    }
  }
}
